/**
 * Per-cycle assessment grid + per-control drawer (P10.4).
 *
 * Rows = controls (from the catalogue), columns = (status / current_ml /
 * target_ml / workflow_state). Click a row to open the drawer for
 * editing + workflow transitions + evidence attach.
 *
 * Mounted at /framework/assessments/:frameworkCode/cycles/:cycleId.
 */
import { Explainer, InfoTip } from "shared/components/Explainer";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Figure } from "shared/components/Figure";

/** Rendered where a control has not been graded yet. */
const EM_DASH = "\u2014";

import { ControlTestingSection } from "framework/assessments/ControlTestingSection";
import { hasPosition, useMe, holdsSeat } from "shared/api/useMe";
import { ControlReviewPanel } from "framework/assessments/ControlReviewPanel";
import { UserPicker } from "shared/components/UserPicker";
import { useDraftCapFromControl } from "shared/recommendations/useRecommendations";
import { ApiError } from "shared/api/client";
import { refusalMessage } from "shared/api/refusalMessage";
import { useToast } from "shared/toast/ToastProvider";
import { stateLabel, statusLabel } from "shared/vocab/workflowState";
import { ControlPlansBlock } from "framework/assessments/ControlPlansBlock";
import { ViewRail } from "shared/onboarding/ViewRail";
import { formatProbePill } from "shared/evidence/probePill";
import type {
  AssessmentAction,
  AssessmentStatus,
  FrameworkAssessment,
  FrameworkControl,
} from "shared/api/types";
import {
  useAssessmentTransition,
  useAttachEvidence,
  useCatalogue,
  useCloseCycle,
  useCycleAssessments,
  useCycles,
  useDetachEvidence,
  useEvidence,
  useOpenCycle,
  usePatchCycle,
  useUpsertAssessment,
} from "framework/api/assessment_hooks";

import { Arrow } from "shared/components/Arrow";
const STATUS_TONE: Record<AssessmentStatus, string> = {
  compliant: "bg-emerald-100 border-emerald-400 text-emerald-900",
  partial: "bg-sky-100 border-sky-400 text-sky-900",
  non_compliant: "bg-rose-100 border-rose-400 text-rose-900",
  no_evidence: "bg-slate-100 border-slate-300 text-slate-700",
};

const WORKFLOW_TONE: Record<string, string> = {
  draft: "bg-slate-50 text-slate-700 border-slate-300",
  in_review: "bg-amber-50 text-amber-900 border-amber-300",
  in_signoff: "bg-violet-50 text-violet-900 border-violet-300",
  approved: "bg-emerald-50 text-emerald-900 border-emerald-300",
  sent_back: "bg-rose-50 text-rose-900 border-rose-300",
};

export function CycleAssessmentGridView() {
  const { t } = useTranslation();
  const { frameworkCode = "", cycleId = "" } = useParams<{
    frameworkCode: string;
    cycleId: string;
  }>();
  const me = useMe();
  const isAdmin = me.data?.is_admin ?? false;
  // P263: the Marsad Engine analyser is now a preparer tool (lifted from
  // admin). Open/close/auto-analyse stay admin; the analyser link follows
  // this flag.
  const canRunEngine = isAdmin || hasPosition(me.data, "preparer");

  const catalogueQuery = useCatalogue(frameworkCode);
  const cyclesQuery = useCycles(frameworkCode);
  const assessmentsQuery = useCycleAssessments(cycleId);
  const openCycle = useOpenCycle();
  const closeCycle = useCloseCycle();
  const patchCycle = usePatchCycle();

  const cycle = useMemo(
    () => cyclesQuery.data?.find((c) => c.id === cycleId),
    [cyclesQuery.data, cycleId],
  );

  const assessmentsByControl = useMemo(() => {
    const map = new Map<string, FrameworkAssessment>();
    for (const a of assessmentsQuery.data ?? []) {
      map.set(a.framework_control_id, a);
    }
    return map;
  }, [assessmentsQuery.data]);

  // Round 10 F1 -- a link may name the control to open. Global search
  // sends a control hit to the hub, which resolves the cycle and
  // forwards here carrying `?control=<uuid>`; without this the reader
  // arrived at a 240-row grid and had to find the row by eye, which is
  // the dead end in a longer corridor.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedControlId = searchParams.get("control");
  const [openControlId, setOpenControlId] = useState<string | null>(requestedControlId);

  useEffect(() => {
    if (requestedControlId) setOpenControlId(requestedControlId);
  }, [requestedControlId]);

  const closeDrawer = useCallback(() => {
    setOpenControlId(null);
    // Drop the parameter as well, or a refresh -- or the reader
    // closing the drawer to look at the grid -- reopens it.
    if (searchParams.has("control")) {
      const next = new URLSearchParams(searchParams);
      next.delete("control");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const allControls = useMemo(() => {
    const list: FrameworkControl[] = [];
    for (const dom of catalogueQuery.data?.domains ?? []) {
      for (const sub of dom.subdomains) {
        for (const ctrl of sub.controls) {
          list.push(ctrl);
        }
      }
    }
    return list;
  }, [catalogueQuery.data]);

  const openControl = openControlId
    ? (allControls.find((c) => c.id === openControlId) ?? null)
    : null;

  // A link naming a control this framework does not have is a link to
  // nothing. Say so rather than rendering the grid as though nothing
  // had been asked for.
  const requestedControlMissing =
    !!requestedControlId &&
    !!catalogueQuery.data &&
    !allControls.some((c) => c.id === requestedControlId);

  return (
    <div
      data-testid="cycle-assessment-grid-view"
      data-framework-code={frameworkCode}
      data-cycle-id={cycleId}
      className="flex flex-col gap-4"
    >
      <ViewRail viewKey="framework_assessments" />
      <header className="space-y-2">
        <Link
          to={`/framework/assessments/${encodeURIComponent(frameworkCode)}`}
          data-testid="back-to-cycles"
          className="text-xs text-brand-brassDark hover:text-brand-brass"
        >
          <Arrow kind="back" />{" "}
          {t("assessmentGrid.backToCycles", { code: frameworkCode.toUpperCase() })}
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900">
          {cycle?.name ?? t("assessmentGrid.cycleFallback")}
        </h1>
        <p className="text-xs text-slate-600">
          {t("assessmentGrid.controlsStatus", {
            count: catalogueQuery.data?.total_controls ?? 0,
          })}{" "}
          <span data-testid="cycle-status-pill" className="font-mono">
            {cycle?.status ?? "—"}
          </span>
        </p>
        {/* F2: the preparer's whole job happens inside a cycle, and
            nothing said what one is. */}
        <Explainer termKey="assessment_cycle" className="max-w-3xl" />
        {cycle && canRunEngine && (
          <div className="flex gap-2">
            {isAdmin && cycle.status === "draft" && (
              <button
                type="button"
                data-testid="cycle-open-button"
                onClick={() => openCycle.mutate({ cycleId })}
                disabled={openCycle.isPending}
                className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
              >
                {t("assessmentGrid.openCycle")}
              </button>
            )}
            {isAdmin && cycle.status === "open" && (
              <button
                type="button"
                data-testid="cycle-close-button"
                onClick={() => closeCycle.mutate({ cycleId })}
                disabled={closeCycle.isPending}
                className="rounded border border-slate-400 bg-slate-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
              >
                {t("assessmentGrid.closeCycle")}
              </button>
            )}
            <Link
              to={`/framework/assessments/${encodeURIComponent(frameworkCode)}/cycles/${encodeURIComponent(cycleId)}/analyser`}
              data-testid="analyser-panel-link"
              className="rounded border border-violet-400 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-900 hover:bg-violet-100"
            >
              {t("screens.cycleAssessmentGridView.marsadEngine")}
            </Link>
            {isAdmin && cycle.status !== "closed" && (
              <label
                data-testid="cycle-auto-analyse-toggle"
                data-auto-analyse-enabled={cycle.auto_analyse_on_evidence_attach}
                className="flex items-center gap-2 rounded border border-indigo-300 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-900"
                title={t("assessmentGrid.autoAnalyseTooltip")}
              >
                <input
                  type="checkbox"
                  data-testid="cycle-auto-analyse-checkbox"
                  checked={cycle.auto_analyse_on_evidence_attach}
                  disabled={patchCycle.isPending}
                  onChange={(e) =>
                    patchCycle.mutate({
                      cycleId,
                      autoAnalyseOnEvidenceAttach: e.target.checked,
                    })
                  }
                  className="h-3 w-3"
                />
                {t("assessmentGrid.autoAnalyse")}
              </label>
            )}
          </div>
        )}
      </header>

      {catalogueQuery.isLoading && (
        <p className="text-xs text-slate-500">{t("assessmentGrid.loadingCatalogue")}</p>
      )}

      {requestedControlMissing && (
        <p
          data-testid="requested-control-missing"
          className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          {t("assessmentGrid.controlNotInThisCycle")}
        </p>
      )}

      {catalogueQuery.data && (
        <div className="space-y-4">
          {catalogueQuery.data.domains.map((dom) => (
            <section
              key={dom.domain_id}
              data-testid="domain-section"
              data-domain-id={dom.domain_id}
              className="rounded border border-slate-200 bg-white"
            >
              <header className="border-b border-slate-200 px-3 py-2">
                <div className="text-sm font-semibold text-slate-900">
                  {dom.domain_name}
                </div>
                <div className="font-mono text-[10px] text-slate-500">
                  {dom.domain_id}
                </div>
              </header>
              <div className="p-2 space-y-3">
                {dom.subdomains.map((sub) => (
                  <div key={sub.subdomain_id} data-testid="subdomain-section">
                    <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      {sub.subdomain_name}
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-start text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="px-2 py-1">
                            {t("assessmentGrid.colControl")}
                          </th>
                          <th className="px-2 py-1 w-32">
                            {t("assessmentGrid.colStatus")}
                            <InfoTip termKey="control_status" />
                          </th>
                          <th className="px-2 py-1 w-20">
                            {t("assessmentGrid.colMl")}
                            <InfoTip termKey="maturity_level" />
                          </th>
                          <th className="px-2 py-1 w-32">
                            {t("assessmentGrid.colWorkflow")}
                            <InfoTip termKey="peer_workflow" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sub.controls.map((ctrl) => {
                          const a = assessmentsByControl.get(ctrl.id);
                          return (
                            <ControlRow
                              key={ctrl.id}
                              control={ctrl}
                              assessment={a}
                              onClick={() => setOpenControlId(ctrl.id)}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {openControl && (
        <AssessmentDrawer
          cycleId={cycleId}
          control={openControl}
          assessment={assessmentsByControl.get(openControl.id)}
          autoAnalyseEnabled={cycle?.auto_analyse_on_evidence_attach ?? false}
          onClose={closeDrawer}
        />
      )}
    </div>
  );
}

interface ControlRowProps {
  control: FrameworkControl;
  assessment: FrameworkAssessment | undefined;
  onClick: () => void;
}

function ControlRow({ control, assessment, onClick }: ControlRowProps) {
  const { t } = useTranslation();
  const status: AssessmentStatus = assessment?.status ?? "no_evidence";
  const tone = STATUS_TONE[status];
  const workflow = assessment?.workflow_state ?? "—";
  return (
    <tr
      data-testid="control-row"
      data-control-id={control.id}
      data-status={status}
      data-workflow={workflow}
      onClick={onClick}
      className="cursor-pointer border-b border-slate-100 hover:bg-brand-brass/10"
    >
      {/* Round 11 E1 -- the cell as well as the line inside it.
          Round 10 put `dir="auto"` on the text div and the RENDERING
          has been right ever since. The cell it sits in still carried
          none, so anything asking the labelled element for its
          direction -- the walk's probe among them -- read null and
          concluded the fix had not landed. Both levels have a real
          answer: the cell resolves from the Latin control identifier
          it leads with, the div from the regulator's sentence. */}
      <td dir="auto" className="px-2 py-1.5">
        <div className="font-mono text-[10px] text-slate-500">
          {control.control_natural_id}
        </div>
        {/* Round 10 E1 -- the regulator's own English inside an RTL
            table. With no `dir`, the cell computes `rtl` over English
            prose and the full stop, and the truncation ellipsis, lead
            the line. `dir="auto"` takes the direction from the text
            rather than from the page. */}
        <div dir="auto" className="text-slate-800 leading-tight">
          {control.control_text.length > 140
            ? control.control_text.slice(0, 140) + "…"
            : control.control_text}
        </div>
      </td>
      <td className="px-2 py-1.5">
        <span
          data-testid="status-pill"
          className={`inline-block rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tone}`}
        >
          {/* Round 10 E1 -- "non compliant" inside an Arabic table.
              De-underscoring an enum is not translating it: the
              catalogue has had these words since round 9 and this
              pill was still printing the column value. */}
          {statusLabel(status)}
        </span>
      </td>
      <td className="px-2 py-1.5 tabular-nums">
        {/* C1 -- one figure, not two numbers with a neutral between
            them. The bidi algorithm folds a separator DIRECTLY between
            two digits into the number (rule W4), which is why "3.1.1"
            and "2026-09-09" survive; put spaces around the separator
            and it no longer applies, the neutrals take the paragraph's
            direction, and the two runs swap. So an Arabic reader was
            told a control at maturity 1 of 4 was at 4 of 1, on the
            screen the whole framework module hangs off. */}
        <Figure>
          {[
            assessment?.current_ml ?? EM_DASH,
            assessment?.target_ml ?? control.default_target_ml,
          ].join(" / ")}
        </Figure>
        {/* Round 12 F5 -- "COMPLIANT 4 / 3" is the row a reviewer reads
            BEFORE he opens anything, and a current maturity above the
            target went through it unmarked. Allowed, and worth a
            second look. */}
        {assessment?.current_ml != null &&
          assessment.current_ml >
            (assessment.target_ml ?? control.default_target_ml) && (
            <span
              data-testid={`grid-above-target-${control.id}`}
              title={t("assessmentGrid.review.aboveTarget", {
                current: assessment.current_ml,
                target: assessment.target_ml ?? control.default_target_ml,
              })}
              className="ms-1 text-[10px] font-semibold text-amber-700"
            >
              {/* ROUND 15 H4 -- "5 / 4above target": a margin is not a
                  separator in text. */}
              {" · "}
              {t("assessmentGrid.aboveTargetMark")}
            </span>
          )}
      </td>
      <td className="px-2 py-1.5">
        <div className="flex items-center gap-1">
          <span
            className={`inline-block rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
              WORKFLOW_TONE[workflow] ?? WORKFLOW_TONE.draft
            }`}
          >
            {/* D2 -- the grid printed the raw enum with its underscore
                swapped for a space, so a reviewer read "in review" as a
                column value while the drawer beside it said "In review"
                from the shared vocabulary. One vocabulary, or the two
                surfaces disagree about the same state. */}
            {stateLabel(workflow) || t("screens.cycleAssessmentGridView.draft")}
          </span>
          {(assessment?.derived_from_tests?.length ?? 0) > 0 && (
            <span
              data-testid="derived-badge"
              title={t("assessmentGrid.derivedTooltip", {
                tests: (assessment?.derived_from_tests ?? [])
                  .map((x) => x.replace(/_/g, " "))
                  .join(", "),
              })}
              className="inline-block rounded border border-sky-300 bg-sky-50 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-sky-900"
            >
              {/* Round 11 E1 -- one vocabulary for both labels on the
                  row. The judgement beside this badge comes from
                  `statusLabel`; the badge came from a string of its
                  own, so two state words one centimetre apart were
                  catalogued in two different places and only one of
                  them was a vocabulary a translator is given. */}
              {statusLabel("tested")}
            </span>
          )}
          {assessment?.derived_from_run_id && (
            <span
              data-testid="ai-drafted-badge"
              title={t("assessmentGrid.aiDraftedTooltip")}
              className="inline-block rounded border border-violet-300 bg-violet-50 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-violet-900"
            >
              {t("screens.cycleAssessmentGridView.ai")}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

// -- Per-control drawer --------------------------------------------

interface DrawerProps {
  cycleId: string;
  control: FrameworkControl;
  assessment: FrameworkAssessment | undefined;
  /**
   * P22.1 (#522): when True the attach form exposes an optional
   * evidence_text textarea so the auto-dispatched Marsad Engine
   * analyser has text to grade against.
   */
  autoAnalyseEnabled: boolean;
  onClose: () => void;
}

const ALL_STATUSES: AssessmentStatus[] = [
  "compliant",
  "partial",
  "non_compliant",
  "no_evidence",
];

/**
 * B10 -- an admin AUDITING a control got the editor: eleven inputs for
 * a judgement he had not come to change, above the reading he had.
 *
 * The admission stays -- an administrator supporting a tenant must be
 * able to correct a grade -- but for anybody who holds no preparer
 * seat the reading leads and the form is one click away.
 */
function EditorFold({
  leadsByDefault,
  children,
}: {
  leadsByDefault: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  if (leadsByDefault) return <>{children}</>;
  return (
    <details data-testid="assessment-editor-collapsed">
      <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
        {t("screens.cycleAssessmentGridView.editThisGrade")}
      </summary>
      <div className="mt-2">{children}</div>
    </details>
  );
}

/** Round 14 A4 -- whichever copy of the row is more recent. */
function newerAssessment(
  a: FrameworkAssessment | null | undefined,
  b: FrameworkAssessment | null | undefined,
): FrameworkAssessment | null | undefined {
  if (!a) return b;
  if (!b || b.id !== a.id) return a;
  return (b.updated_at ?? "") > (a.updated_at ?? "") ? b : a;
}

function AssessmentDrawer({
  cycleId,
  control,
  assessment,
  autoAnalyseEnabled,
  onClose,
}: DrawerProps) {
  const { t } = useTranslation();
  const upsert = useUpsertAssessment(cycleId);
  const transition = useAssessmentTransition(cycleId);
  // The assessment this drawer is bound to: whatever the parent handed
  // down, or whatever the last save returned -- see handleSave.
  const [saved, setSaved] = useState<FrameworkAssessment | null>(null);
  // ROUND 14 A4 -- the NEWER of the two, not "saved if there is one". A
  // write made elsewhere on the drawer -- Undo on the review panel, an
  // Engine accept -- reaches this component only through the parent's
  // refetch, and `saved ?? assessment` let an earlier save in the same
  // drawer hide it.
  const live = newerAssessment(saved, assessment);
  const evidence = useEvidence(live?.id);
  const attachEvidence = useAttachEvidence(live?.id ?? "");
  const detachEvidence = useDetachEvidence(live?.id ?? "");
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const draftCap = useDraftCapFromControl();

  const [status, setStatus] = useState<AssessmentStatus>(
    assessment?.status ?? "no_evidence",
  );
  const [currentMl, setCurrentMl] = useState<number | "">(assessment?.current_ml ?? "");
  const [evidenceSummary, setEvidenceSummary] = useState<string>(
    assessment?.evidence_summary ?? "",
  );
  const [gaps, setGaps] = useState<string>(assessment?.gaps ?? "");
  const [assigneeUserId, setAssigneeUserId] = useState<string | null>(
    assessment?.assignee_user_id ?? null,
  );
  const [approverUserId, setApproverUserId] = useState<string | null>(
    assessment?.approver_user_id ?? null,
  );
  // ROUND 14 A4 -- the form follows the record. It copied the assessment
  // once, at mount, so after Undo the header showed her grade while the
  // fields underneath still held the Engine's maturity and gaps -- and
  // her next Save wrote them onto the record under "Graded by" her, with
  // the Engine's line removed, and sent them to the reviewer. Whenever
  // the record behind the drawer changes, the form shows what it now
  // says. (react-query keeps the same object while the data is equal, so
  // an idle refetch does not touch what she is typing.)
  useEffect(() => {
    if (!live) return;
    setStatus(live.status);
    setCurrentMl(live.current_ml ?? "");
    setEvidenceSummary(live.evidence_summary ?? "");
    setGaps(live.gaps ?? "");
  }, [live]);
  const [newUri, setNewUri] = useState<string>("");
  const [newEvidenceText, setNewEvidenceText] = useState<string>("");
  const [transitionComment, setTransitionComment] = useState<string>("");

  const handleSave = async () => {
    try {
      const saved = await upsert.mutateAsync({
        controlId: control.id,
        body: {
          status,
          current_ml: currentMl === "" ? null : Number(currentMl),
          evidence_summary: evidenceSummary || null,
          gaps: gaps || null,
          // Send only when chosen -- null/omit leaves the column untouched.
          ...(assigneeUserId ? { assignee_user_id: assigneeUserId } : {}),
          ...(approverUserId ? { approver_user_id: approverUserId } : {}),
        },
      });
      // Bind to what came back rather than waiting for the parent's
      // refetch to hand the row down. On a control with no assessment
      // yet, everything keyed on the assessment -- evidence, the
      // workflow, the CAP bridge -- has nothing to key on until this
      // lands, and the operator is left pressing a button that
      // appears to do nothing.
      setSaved(saved);
      toast.push({ tone: "success", message: t("assessmentGrid.savedToast") });
    } catch (e) {
      toast.push({
        tone: "error",
        message: e instanceof Error ? e.message : t("assessmentGrid.saveFailed"),
      });
    }
  };

  const handleAttach = async () => {
    if (!newUri.trim() || !live) return;
    try {
      await attachEvidence.mutateAsync({
        uri: newUri.trim(),
        evidence_text: newEvidenceText.trim() || null,
      });
      setNewUri("");
      setNewEvidenceText("");
      toast.push({ tone: "success", message: t("assessmentGrid.attachedToast") });
    } catch (e) {
      // The duplicate guard answers here, and it is the one message the
      // operator most needs -- silently adding a second identical row
      // reads as two pieces of evidence when there is one.
      toast.push({
        tone: "error",
        message: e instanceof Error ? e.message : t("assessmentGrid.attachFailed"),
      });
    }
  };

  const handleTransition = async (action: AssessmentAction) => {
    if (!live) return;
    // B1 -- the drawer discarded what the transition returned, so
    // `live` kept the pre-transition row: a successful Submit left the
    // button live on a DRAFT that was no longer a draft, and pressing
    // it again 409'd in silence. The endpoint hands back the updated
    // assessment; bind to it, exactly as handleSave does.
    const updated = await transition.mutateAsync({
      assessmentId: live.id,
      action,
      body: { comment: transitionComment || null },
    });
    setSaved(updated);
    setTransitionComment("");
    toast.push({
      tone: "success",
      // D9 -- it named the STATE ("It is now In review"), which says
      // what the record is called rather than where it went. The
      // reader's question is who has it now.
      message: t(`assessmentGrid.transition.${action}`),
    });
  };

  const workflowState = live?.workflow_state ?? null;

  // B1 -- the verbs are the SEAT's, not the state's. A preparer whose
  // assessment had gone for review was still offered Approve and Send
  // back, which are the reviewer's; the API refused, after the click.
  const isPreparer = Boolean(me.data?.is_admin || hasPosition(me.data, "preparer"));
  const isReviewer = Boolean(
    me.data?.is_admin ||
    hasPosition(me.data, "reviewer") ||
    hasPosition(me.data, "approver"),
  );

  // P262: bridge assess -> remediate. A non-compliant / partial control can
  // raise a remediation CAP pre-linked to itself (+ Engine-drafted actions
  // when configured), then jump to the new CAP. Reuses the
  // draft-from-control route the Recommendations inbox already drives.
  // The seat that grades a control. An admin is admitted because an
  // administrator supporting a tenant must be able to correct one.
  const canEditJudgement = Boolean(
    me.data?.is_admin || hasPosition(me.data, "preparer"),
  );
  // B10 -- an admin AUDITING a control got the editor: eleven inputs
  // for a judgement he had not come to change.
  //
  // The admission itself is right and stays -- an administrator
  // supporting a tenant must be able to correct a grade. What was
  // wrong is that editing was the DEFAULT for somebody who holds no
  // preparer seat, so the reading he came for sat under a form. The
  // reading leads; the editor is one click away and unchanged.
  //
  // Round 9 E3 -- and this used `hasPosition`, which admits an admin
  // for EVERY position. So `editorLeadsByDefault` was true for exactly
  // the seat B10 was written for, and the fold it added never rendered
  // once. A fix that reads correctly and is inert is worse than none,
  // because it closes the finding.
  //
  // `holdsSeat` is the question actually being asked -- does this
  // person hold the preparer seat -- and it exists for precisely this
  // distinction, which A2/A4 drew and the server draws too. Sixth
  // round for this shape, and the helper had been sitting there for
  // one of them.
  const editorLeadsByDefault = holdsSeat(me.data, "preparer");

  const canRemediate =
    (status === "non_compliant" || status === "partial") &&
    (me.data?.is_admin || hasPosition(me.data, "preparer"));
  // ROUND 15 A18 -- the route refuses a second plan for a control whose
  // first has moved past draft, naming the plan. The next press, made
  // with the name on screen, is the answer.
  const [askedAgain, setAskedAgain] = useState<string | null>(null);
  // Round 18 A33 -- the plan the control is already on, to open it.
  const [onPlan, setOnPlan] = useState<string | null>(null);
  /* Round 19 G41 -- a plan raised from a control could not be named. */
  const [planTitle, setPlanTitle] = useState("");
  const handleRaiseCap = () => {
    draftCap.mutate(
      {
        controlId: control.id,
        withEngine: true,
        force: askedAgain !== null,
        title: planTitle,
      },
      {
        onSuccess: (r) => {
          // ROUND 14 A5 -- the control was already on a draft plan. Say
          // so, rather than "Remediation CAP drafted" over a plan that
          // existed before the press and had nothing added to it.
          if (r.created === false) {
            toast.push({ tone: "info", message: t("recommendations.reusedDraft") });
            navigate(`/framework/caps/${r.cap_id}`);
            return;
          }
          const actions =
            "actions" in r && typeof r.actions === "number" ? r.actions : 0;
          // ROUND 16 A22 -- the Engine was asked and drafted nothing.
          // "Remediation CAP drafted" over an empty plan was the toast;
          // the plan said "No action items yet." and nothing else.
          /* Round 19 G41 -- the plan's page says this on arrival; a toast
             here printed it a second time. */
          if ("engineRefused" in r && r.engineRefused) {
            navigate(`/framework/caps/${r.cap_id}`, { state: { draftRefused: true } });
            return;
          }
          toast.push({
            tone: "success",
            message: "Remediation CAP drafted",
            detail:
              actions > 0
                ? `${actions} Engine-drafted actions; control pre-linked`
                : "engineOff" in r && r.engineOff
                  ? "Engine off -- add actions manually"
                  : "Control pre-linked; add actions",
          });
          /* ROUND 13 F7 -- carry the grounding verdict to the page
             the operator lands on. This path drafted actions citing
             exactly one control and printed no grounding line at all,
             while the CAP's own drafting verb printed one carrying
             the nominal-attribution warning. Round 12 recorded the
             same asymmetry and it stood. */
          navigate(`/framework/caps/${r.cap_id}`, {
            state: r.grounding ? { draftGrounding: r.grounding } : undefined,
          });
        },
        onError: (e) => {
          if (
            e instanceof ApiError &&
            (e.code === "refusal.control_on_open_plan" ||
              e.code === "refusal.control_on_draft_plan")
          ) {
            setAskedAgain(refusalMessage(t, e));
            setOnPlan(e.params?.cap_id ?? null);
            return;
          }
          toast.push({ tone: "error", message: refusalMessage(t, e) });
        },
      },
    );
  };

  return (
    <div
      onClick={onClose}
      data-testid="assessment-drawer"
      data-control-id={control.id}
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/30 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-slate-300 bg-white p-4 shadow-xl space-y-3"
      >
        <header className="flex items-baseline justify-between">
          <div>
            <div className="text-sm font-bold text-slate-900">
              {control.control_natural_id}
            </div>
            {/* Round 11 E1 -- the drawer renders the same regulator
                sentence as the grid cell and had no `dir` at all. */}
            <p dir="auto" className="text-xs text-slate-600 mt-0.5">
              {control.control_text}
            </p>
          </div>
          <button
            type="button"
            data-testid="drawer-close-button"
            onClick={onClose}
            className="text-[10px] uppercase tracking-wider text-slate-700 hover:underline"
          >
            {t("assessmentGrid.close")}
          </button>
        </header>
        {/* Round 18 A34 -- the plans remediating it, and their letters. */}
        <ControlPlansBlock controlId={control.id} />

        {/* E6 -- an auditor opening a control got the preparer's
            EDITING panel: form controls he cannot use, and none of what
            he came for. Whether he may edit decides which panel he
            sees, not merely whether the save button works.

            D11 -- and the administrator got the editor INSTEAD of the
            reading, so the one seat most likely to be showing a control
            to somebody else could not see who graded it or what it
            rests on. Editing is an addition to reading, not a
            replacement for it. */}
        <ControlReviewPanel
          control={control}
          live={live}
          cycleId={cycleId}
          evidence={evidence.data ?? []}
        />

        {/* Editable judgement */}
        {canEditJudgement && (
          <EditorFold leadsByDefault={editorLeadsByDefault}>
            <fieldset className="space-y-2 rounded border border-slate-200 p-3">
              <legend className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1">
                {t("assessmentGrid.judgement")}
              </legend>
              <label className="block text-xs">
                <span className="text-slate-600">{t("assessmentGrid.status")}</span>
                <select
                  data-testid="drawer-status-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as AssessmentStatus)}
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
                >
                  {/* F -- `s.replace("_", " ")` turned no_evidence into
                    "no evidence" and left non_compliant as "non
                    compliant": a partial de-slugging that reads as a
                    typo. The catalogue names them. */}
                  {ALL_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {statusLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs">
                <span className="text-slate-600">{t("assessmentGrid.currentMl")}</span>
                <input
                  data-testid="drawer-ml-input"
                  type="number"
                  min={1}
                  max={5}
                  value={currentMl}
                  onChange={(e) =>
                    setCurrentMl(e.target.value === "" ? "" : Number(e.target.value))
                  }
                  className="mt-1 block w-20 rounded border border-slate-300 px-2 py-1 text-xs"
                />
              </label>
              <label className="block text-xs">
                <span className="text-slate-600">
                  {t("assessmentGrid.evidenceSummary")}
                </span>
                <textarea
                  dir="auto"
                  data-testid="drawer-evidence-summary"
                  value={evidenceSummary}
                  onChange={(e) => setEvidenceSummary(e.target.value)}
                  rows={2}
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
                />
              </label>
              <label className="block text-xs">
                <span className="text-slate-600">{t("assessmentGrid.gaps")}</span>
                <textarea
                  dir="auto"
                  data-testid="drawer-gaps"
                  value={gaps}
                  onChange={(e) => setGaps(e.target.value)}
                  rows={2}
                  className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
                />
              </label>
              <UserPicker
                testid="drawer-assignee"
                label={t("assessmentGrid.assignee")}
                selectedLabel={
                  assigneeUserId ? t("assessmentGrid.currentlyAssigned") : null
                }
                onSelect={(u) => setAssigneeUserId(u.id)}
              />
              <UserPicker
                testid="drawer-approver"
                label={t("assessmentGrid.approver")}
                selectedLabel={
                  approverUserId ? t("assessmentGrid.currentlyAssigned") : null
                }
                onSelect={(u) => setApproverUserId(u.id)}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid="drawer-save-button"
                  onClick={() => void handleSave()}
                  disabled={upsert.isPending}
                  className="rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
                >
                  {upsert.isPending
                    ? t("assessmentGrid.saving")
                    : t("assessmentGrid.saveJudgement")}
                </button>
                {canRemediate && (
                  <input
                    data-testid="drawer-raise-cap-title"
                    aria-label={t("assessmentGrid.planTitle")}
                    placeholder={t("assessmentGrid.planTitlePlaceholder")}
                    value={planTitle}
                    onChange={(e) => setPlanTitle(e.target.value)}
                    className="min-w-[16rem] rounded border border-slate-300 px-2 py-1 text-xs"
                  />
                )}{" "}
                {canRemediate && (
                  <button
                    type="button"
                    data-testid="drawer-raise-cap"
                    onClick={handleRaiseCap}
                    disabled={draftCap.isPending}
                    className="rounded border border-brand-brass/40 bg-brand-brass/5 px-3 py-1 text-xs font-semibold text-brand-brassDark hover:bg-brand-brass/10 disabled:opacity-50"
                  >
                    {draftCap.isPending
                      ? t("assessmentGrid.drafting")
                      : askedAgain
                        ? t("assessmentGrid.raiseAnother")
                        : t("assessmentGrid.raiseCap")}
                  </button>
                )}
              </div>
              {canRemediate && askedAgain && (
                <p
                  data-testid={`grid-raise-cap-asked-${control.id}`}
                  className="text-[11px] text-amber-900"
                >
                  {askedAgain}
                  {onPlan && (
                    <>
                      {" "}
                      <Link
                        to={`/framework/caps/${onPlan}`}
                        data-testid={`grid-raise-cap-open-${control.id}`}
                        className="font-medium text-brand-brassDark hover:underline"
                      >
                        {t("assessmentGrid.openThatPlan")}
                      </Link>
                    </>
                  )}
                </p>
              )}
              {canRemediate && (
                <p className="text-[11px] text-slate-500">
                  {t("screens.cycleAssessmentGridView.thisControlIs")}{" "}
                  {status === "non_compliant"
                    ? t("screens.cycleAssessmentGridView.nonCompliant2")
                    : t("screens.cycleAssessmentGridView.partial")}
                  {t("screens.cycleAssessmentGridView.raiseACapPreLinkedToIt")}
                </p>
              )}
            </fieldset>

            {/* Control testing (P279): ToD/ToI/ToE + the worst-of
                rollup.

                E6 said the read panel landed for a non-preparer and
                then every editor followed beneath it, and that half a
                fix reads worse than none. E9 -- this is the other
                half. The fold reached the judgement fieldset alone,
                so an administrator reading a control still met three
                test rows, a probe panel and a KPI panel open beneath
                it: eleven inputs, under a heading that had just
                finished telling him this was a reading.

                One disclosure, covering everything that changes the
                control. Inside it, so opening the editor is a single
                deliberate act rather than a form that was already
                there. */}
            <ControlTestingSection cycleId={cycleId} controlId={control.id} />
          </EditorFold>
        )}

        {/* Workflow transitions */}
        {live && (
          <fieldset className="space-y-2 rounded border border-slate-200 p-3">
            <legend className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1">
              {t("screens.cycleAssessmentGridView.workflow")}
              {live.workflow_pattern}
              {t("screens.cycleAssessmentGridView.state")}{" "}
              {/* F -- this printed the stored value, so the legend read
                  "state in_review" while everything else on the page
                  said "In review". */}
              {stateLabel(workflowState) || t("screens.cycleAssessmentGridView.draft")}
            </legend>
            <label className="block text-xs">
              <span className="text-slate-600">{t("assessmentGrid.comment")}</span>
              <textarea
                dir="auto"
                data-testid="drawer-transition-comment"
                value={transitionComment}
                onChange={(e) => setTransitionComment(e.target.value)}
                rows={1}
                className="mt-1 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              {(workflowState === "draft" || workflowState === null) && isPreparer && (
                <button
                  type="button"
                  data-testid="drawer-submit-button"
                  onClick={() => void handleTransition("submit")}
                  className="rounded border border-amber-400 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900"
                >
                  {t("assessmentGrid.submitForReview")}
                </button>
              )}
              {(workflowState === "in_review" || workflowState === "in_signoff") &&
                isReviewer && (
                  <>
                    <button
                      type="button"
                      data-testid="drawer-approve-button"
                      onClick={() => void handleTransition("approve")}
                      className="rounded border border-emerald-400 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-900"
                    >
                      {t("assessmentGrid.approve")}
                    </button>
                    <button
                      type="button"
                      data-testid="drawer-sendback-button"
                      onClick={() => void handleTransition("send_back")}
                      className="rounded border border-rose-400 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-900"
                    >
                      {t("assessmentGrid.sendBack")}
                    </button>
                  </>
                )}
              {(workflowState === "in_review" || workflowState === "in_signoff") &&
                isPreparer && (
                  <button
                    type="button"
                    data-testid="drawer-withdraw-button"
                    onClick={() => void handleTransition("withdraw")}
                    className="rounded border border-slate-400 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-900"
                  >
                    {t("assessmentGrid.withdraw")}
                  </button>
                )}
              {workflowState === "approved" && (
                <span
                  data-testid="drawer-approved-badge"
                  className="rounded border border-emerald-400 bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-900"
                >
                  {t("assessmentGrid.approvedNoActions")}
                </span>
              )}
            </div>
          </fieldset>
        )}

        {/* Evidence */}
        {live && (
          <fieldset className="space-y-2 rounded border border-slate-200 p-3">
            <legend className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-1">
              {t("assessmentGrid.evidence")}
            </legend>
            <ul data-testid="drawer-evidence-list" className="space-y-1">
              {(evidence.data ?? []).map((e) => {
                const pill = formatProbePill(
                  e.last_probe_status,
                  e.last_probed_at,
                  e.last_probe_error,
                );
                return (
                  <li
                    key={e.link_id}
                    data-testid="evidence-row"
                    data-link-id={e.link_id}
                    className="flex items-center justify-between rounded border border-slate-200 bg-white px-2 py-1 text-xs gap-2"
                  >
                    {/* A bare URL is not a title. Prefer the filename,
                        then the note the attacher wrote, and always say
                        WHO attached it -- an evidence row nobody can
                        attribute is an evidence row nobody can chase. */}
                    <span className="flex-1 min-w-0">
                      <a
                        href={e.uri}
                        target="_blank"
                        rel="noreferrer"
                        title={e.uri}
                        className="block truncate text-brand-brassDark hover:underline"
                      >
                        {e.filename ?? e.notes ?? e.uri}
                      </a>
                      {/* The artefact's own page (P75) -- its linked
                          assessments, linked findings and freshness
                          history. It had NO inbound link anywhere in
                          the SPA until round 10 measured the routes:
                          the page shipped, and the only thing that
                          ever pointed near it was cross-entity
                          search, which sends an artefact to its
                          external uri. The link above still goes to
                          the document; this one goes to what MARSAD
                          knows about it. */}
                      <Link
                        to={`/twin/evidence/${e.artefact_id}`}
                        data-testid="evidence-detail-link"
                        className="block truncate text-[10px] text-brand-brassDark hover:underline"
                      >
                        {t("assessmentGrid.evidenceDetails")}
                      </Link>
                      <span
                        data-testid="evidence-attribution"
                        className="block truncate text-[10px] text-slate-400"
                      >
                        {e.uploader_email
                          ? t("assessmentGrid.attachedBy", { who: e.uploader_email })
                          : t("assessmentGrid.attachedByUnknown")}
                      </span>
                      {/* Round 20 Y4 -- how old it is, by what date, and
                          whether it predates the cycle it is evidence in. */}
                      {e.dated_on && (
                        <span
                          data-testid="evidence-dated"
                          data-older={e.older_than_cycle ? "true" : "false"}
                          className={`block truncate text-[10px] ${
                            e.older_than_cycle
                              ? "font-medium text-rag-amber"
                              : "text-slate-400"
                          }`}
                        >
                          {t(
                            e.date_basis === "uploaded"
                              ? "assessmentGrid.evidenceUploaded"
                              : "assessmentGrid.evidenceRetrieved",
                            { date: e.dated_on },
                          )}
                          {e.older_than_cycle &&
                            ` -- ${t("assessmentGrid.evidenceOlderThanCycle")}`}
                        </span>
                      )}
                    </span>
                    {pill && (
                      <span
                        data-testid="evidence-probe-pill"
                        data-probe-status={e.last_probe_status ?? "null"}
                        title={pill.tooltip}
                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums whitespace-nowrap ${pill.tone}`}
                      >
                        {pill.label}
                      </span>
                    )}
                    {/* E6 -- detaching somebody else's evidence is not
                        a reader's act. */}
                    {canEditJudgement && (
                      <button
                        type="button"
                        data-testid="evidence-detach-button"
                        onClick={() => detachEvidence.mutate({ linkId: e.link_id })}
                        className="text-[10px] uppercase tracking-wider text-rose-700 hover:underline whitespace-nowrap"
                      >
                        {t("assessmentGrid.detach")}
                      </button>
                    )}
                  </li>
                );
              })}
              {(evidence.data ?? []).length === 0 && (
                <li className="text-xs italic text-slate-500">
                  {t("assessmentGrid.noEvidence")}
                </li>
              )}
            </ul>
            {/* E6 -- attaching evidence is the preparer's act. A
                reviewer auditing the control reads what is there. */}
            {canEditJudgement && (
              <div className="flex flex-col gap-2">
                <input
                  dir="auto"
                  data-testid="drawer-evidence-uri-input"
                  type="url"
                  placeholder={t("assessmentGrid.uriPlaceholder")}
                  value={newUri}
                  onChange={(e) => setNewUri(e.target.value)}
                  className="rounded border border-slate-300 px-2 py-1 text-xs"
                />
                {autoAnalyseEnabled && (
                  <textarea
                    dir="auto"
                    data-testid="drawer-evidence-text-input"
                    placeholder={t("assessmentGrid.evidenceTextPlaceholder")}
                    value={newEvidenceText}
                    onChange={(e) => setNewEvidenceText(e.target.value)}
                    rows={3}
                    className="rounded border border-indigo-200 bg-indigo-50/30 px-2 py-1 text-xs"
                  />
                )}
                <button
                  type="button"
                  data-testid="drawer-attach-evidence-button"
                  onClick={() => void handleAttach()}
                  disabled={!newUri.trim() || attachEvidence.isPending}
                  className="self-end rounded border border-brand-brassDark bg-brand-brass px-3 py-1 text-xs font-semibold text-brand-navy disabled:opacity-50"
                >
                  {t("assessmentGrid.attach")}
                </button>
              </div>
            )}
          </fieldset>
        )}
      </div>
    </div>
  );
}
